import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RequirementOrganizer from '../../components/RequirementOrganizer';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { 
  Building2, Plus, Briefcase, Users, DollarSign, Calendar, Clock, MapPin,
  Star, Check, X, AlertTriangle, CreditCard, Zap, Settings, FileText
} from 'lucide-react';
import { formatTimeRange, formatDateMonthDay } from '../../utils/timeFormat';
import { toast } from 'sonner';

// SUBSCRIPTION_PLANS constant removed — subscription management is between
// iEvntür and the agency, not exposed in the workspace UI.

const AgencyDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('jobs');
  
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    event_type: '',
    role_id: '',
    venue_name: '',
    venue_address: '',
    city: '',
    state: '',
    job_date: '',
    start_time: '',
    end_time: '',
    hourly_rate: '',
    workers_needed: 1,
    dress_code: '',
    responsibilities: '',
    arrival_instructions: '',
    requirements: '',
    // Event Briefing — Iter-229: surfaces directly in the talent booking experience.
    event_briefing: {
      grooming: '',
      parking_info: '',
      contact_name: '',
      contact_role: '',
      contact_phone: '',
      contact_email: '',
      sent_home_policy: '',
      reporting_instructions: '',
      required_documents: '',
      certifications: '',
    },
  });
  const [posting, setPosting] = useState(false);
  // billingStatus state removed — billing managed by platform admins.

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, jobsRes, rolesRes] = await Promise.all([
        api.get('/agency/profile'),
        api.get('/agency/jobs'),
        api.get('/roles'),
      ]);
      setProfile(profileRes.data);
      setJobs(jobsRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePostJob = async (e) => {
    e.preventDefault();
    setPosting(true);
    
    try {
      const res = await api.post('/agency/jobs', {
        ...jobForm,
        hourly_rate: parseFloat(jobForm.hourly_rate),
        workers_needed: parseInt(jobForm.workers_needed)
      });
      
      if (res.data.payment_required) {
        toast.info('Job created! Payment required to activate.');
        // In a real app, redirect to payment
      } else {
        toast.success('Job posted successfully!');
      }
      
      setShowJobForm(false);
      setJobForm({
        title: '', description: '', event_type: '', role_id: '', venue_name: '', venue_address: '',
        city: '', state: '', job_date: '', start_time: '', end_time: '',
        hourly_rate: '', workers_needed: 1, dress_code: '', responsibilities: '',
        arrival_instructions: '', requirements: '',
        event_briefing: {
          grooming: '', parking_info: '', contact_name: '', contact_role: '',
          contact_phone: '', contact_email: '', sent_home_policy: '',
          reporting_instructions: '', required_documents: '', certifications: '',
        },
      });
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to post job';
      toast.error(msg);
    } finally {
      setPosting(false);
    }
  };

  const handleAcceptApplicant = async (jobId, applicationId) => {
    try {
      await api.post(`/agency/jobs/${jobId}/accept/${applicationId}`);
      toast.success('Applicant accepted!');
      
      // Refresh job details
      const res = await api.get(`/agency/jobs/${jobId}`);
      setSelectedJob(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept applicant');
    }
  };

  const loadJobDetails = async (jobId) => {
    try {
      const res = await api.get(`/agency/jobs/${jobId}`);
      setSelectedJob(res.data);
    } catch (error) {
      toast.error('Failed to load job details');
    }
  };

  // handleSubscribe REMOVED — subscription management is between iEvntür and the agency,
  // not exposed in the workspace UI.

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="agency-dashboard">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-cyan-600" />
                <h1 className="text-2xl font-bold text-gray-900">{profile?.company_name}</h1>
              </div>
              <p className="text-gray-500 mt-1">Agency Workspace • Self-Serve Hiring</p>
            </div>
            <div className="flex gap-3">
              {/* Iter-228 — quick-links to recruitment workspace */}
              <Button variant="outline" onClick={() => navigate('/agency/settings')} data-testid="agency-settings-btn" title="Settings & Appearance">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" onClick={() => navigate('/agency/recruitment')} data-testid="goto-recruitment-btn">
                Recruitment Page
              </Button>
              <Button variant="outline" onClick={() => navigate('/agency/talent-pool')} data-testid="goto-talent-pool-btn">
                Talent Pool
              </Button>
              <Button variant="outline" onClick={() => navigate('/agency/crm')} data-testid="goto-crm-btn">
                <Users className="w-4 h-4 mr-2" />
                Leads & CRM
              </Button>
              <Button variant="outline" onClick={() => navigate('/agency/team')} data-testid="goto-team-btn">
                <Users className="w-4 h-4 mr-2" />
                Team
              </Button>
              <Button variant="outline" onClick={() => navigate('/agency/transactions')} data-testid="goto-transactions-btn">
                <FileText className="w-4 h-4 mr-2" />
                Transactions
              </Button>
              <Button 
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={() => setShowJobForm(true)}
                data-testid="post-job-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post a Job
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Billing-restriction notice REMOVED — subscription/billing is managed
            by platform admins, not exposed in the agency workspace. */}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{profile?.stats?.active_jobs || 0}</p>
                  <p className="text-sm text-gray-500">Active Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{profile?.stats?.filled_jobs || 0}</p>
                  <p className="text-sm text-gray-500">Filled Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Check className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{profile?.stats?.completed_jobs || 0}</p>
                  <p className="text-sm text-gray-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Current Plan card REMOVED — subscription is managed outside the
              agency workspace by platform admins. */}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="jobs">My Jobs</TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="mt-6">
            {jobs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs posted yet</h3>
                  <p className="text-gray-500 mb-4">Post your first job to start finding talent</p>
                  <Button 
                    onClick={() => setShowJobForm(true)}
                    className="bg-cyan-500 hover:bg-cyan-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Post a Job
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4" data-testid="jobs-list">
                {jobs.map((job) => (
                  <Card 
                    key={job.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => loadJobDetails(job.id)}
                    data-testid="job-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                            <Badge variant={job.status === 'open' ? 'default' : job.status === 'filled' ? 'secondary' : 'outline'}>
                              {job.status}
                            </Badge>
                            {job.status === 'pending_payment' && (
                              <Badge className="bg-amber-100 text-amber-800">Payment Required</Badge>
                            )}
                          </div>
                          <div className="space-y-1 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{job.venue_name} • {job.city}, {job.state}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDateMonthDay(job.job_date)}</span>
                              <Clock className="w-4 h-4 ml-2" />
                              <span>{formatTimeRange(job.start_time, job.end_time)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-cyan-600">${job.hourly_rate}/hr</div>
                          <div className="text-sm text-gray-500 mt-1">
                            {job.hired_count}/{job.workers_needed} hired • {job.application_count} applicants
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Subscription tab REMOVED per user request — Subscription management
              remains between iEvntür and the agency, not exposed in the workspace UI. */}
        </Tabs>
      </div>

      {/* Post Job Dialog */}
      <Dialog open={showJobForm} onOpenChange={setShowJobForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post a New Job</DialogTitle>
            <DialogDescription>
              Create a job posting for your event — add as many staff as you need.
            </DialogDescription>
          </DialogHeader>

          {/* Platform notice */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-slate-800">Keep it on iEvntür</p>
              <p className="text-slate-600">
                Bookings, communications and payments for this job stay within iEvntür.
              </p>
            </div>
          </div>

          <form onSubmit={handlePostJob} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  value={jobForm.title}
                  onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                  placeholder="e.g., Bartender for Corporate Event"
                  required
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <Label htmlFor="role">Role Type</Label>
                <Select 
                  value={jobForm.role_id} 
                  onValueChange={(v) => setJobForm({ ...jobForm, role_id: v })}
                  required
                >
                  <SelectTrigger data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="workers">Talent Needed</Label>
                <Input
                  id="workers"
                  type="number"
                  min="1"
                  value={jobForm.workers_needed}
                  onChange={(e) => setJobForm({ ...jobForm, workers_needed: e.target.value })}
                  required
                  data-testid="workers-input"
                />
              </div>

              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Select 
                  value={jobForm.event_type} 
                  onValueChange={(v) => setJobForm({ ...jobForm, event_type: v })}
                  required
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="wedding">Wedding</SelectItem>
                    <SelectItem value="private">Private Party</SelectItem>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                    <SelectItem value="sports">Sports Event</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="venue">Venue Name</Label>
                <Input
                  id="venue"
                  value={jobForm.venue_name}
                  onChange={(e) => setJobForm({ ...jobForm, venue_name: e.target.value })}
                  placeholder="e.g., Downtown Convention Center"
                  required
                  data-testid="venue-input"
                />
              </div>

              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={jobForm.city}
                  onChange={(e) => setJobForm({ ...jobForm, city: e.target.value })}
                  required
                  data-testid="city-input"
                />
              </div>

              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={jobForm.state}
                  onChange={(e) => setJobForm({ ...jobForm, state: e.target.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="CA"
                  required
                  data-testid="state-input"
                />
              </div>

              <div>
                <Label htmlFor="job_date">Date</Label>
                <Input
                  id="job_date"
                  type="date"
                  value={jobForm.job_date}
                  onChange={(e) => setJobForm({ ...jobForm, job_date: e.target.value })}
                  required
                  data-testid="date-input"
                />
              </div>

              <div>
                <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={jobForm.hourly_rate}
                  onChange={(e) => setJobForm({ ...jobForm, hourly_rate: e.target.value })}
                  required
                  data-testid="rate-input"
                />
              </div>

              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={jobForm.start_time}
                  onChange={(e) => setJobForm({ ...jobForm, start_time: e.target.value })}
                  required
                  data-testid="start-time-input"
                />
              </div>

              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={jobForm.end_time}
                  onChange={(e) => setJobForm({ ...jobForm, end_time: e.target.value })}
                  required
                  data-testid="end-time-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="description">Full Job Description *</Label>
                <Textarea
                  id="description"
                  value={jobForm.description}
                  onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  placeholder="Provide a detailed description of the job, expectations, and any important information..."
                  rows={3}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="responsibilities">Responsibilities *</Label>
                <Textarea
                  id="responsibilities"
                  value={jobForm.responsibilities}
                  onChange={(e) => setJobForm({ ...jobForm, responsibilities: e.target.value })}
                  placeholder="List the specific job responsibilities and duties..."
                  rows={3}
                  required
                  data-testid="responsibilities-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="dress_code">Dress Code / Appearance Requirements *</Label>
                <Textarea
                  id="dress_code"
                  value={jobForm.dress_code}
                  onChange={(e) => setJobForm({ ...jobForm, dress_code: e.target.value })}
                  placeholder="Describe the required dress code and any appearance requirements..."
                  rows={2}
                  required
                  data-testid="dress-code-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="arrival_instructions">Arrival Instructions (optional)</Label>
                <Textarea
                  id="arrival_instructions"
                  value={jobForm.arrival_instructions}
                  onChange={(e) => setJobForm({ ...jobForm, arrival_instructions: e.target.value })}
                  placeholder="Where to check in, parking info, who to ask for, etc..."
                  rows={2}
                  data-testid="arrival-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="requirements">Qualifications Required (optional)</Label>
                <Textarea
                  id="requirements"
                  value={jobForm.requirements}
                  onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                  placeholder="List any specific qualifications, certifications, or experience required..."
                  rows={2}
                  data-testid="requirements-input"
                />
              </div>

              {/* ───────────────── Event Briefing (Iter-229) ───────────────── */}
              <div className="col-span-2 mt-2 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Event Briefing</h3>
                  <span className="text-[10px] font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2 py-0.5">Shown to talent</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">Everything in this section flows directly into the talent&apos;s booking detail page (Dress, Grooming, Parking, Event Contact, Sent-Home Policy, Reporting, Documents).</p>
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="grooming">Grooming Requirements</Label>
                <Textarea
                  id="grooming"
                  value={jobForm.event_briefing.grooming}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, grooming: e.target.value } })}
                  placeholder="Hair pulled back, trimmed facial hair, no visible piercings other than ears…"
                  rows={2}
                  data-testid="briefing-grooming"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="parking_info">Parking Instructions</Label>
                <Textarea
                  id="parking_info"
                  value={jobForm.event_briefing.parking_info}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, parking_info: e.target.value } })}
                  placeholder="Free street parking on Ivy Lane. Lot B validated with shift QR…"
                  rows={2}
                  data-testid="briefing-parking"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="contact_name">Event Contact Name</Label>
                <Input
                  id="contact_name"
                  value={jobForm.event_briefing.contact_name}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, contact_name: e.target.value } })}
                  placeholder="Maria Santos"
                  data-testid="briefing-contact-name"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="contact_role">Event Contact Role</Label>
                <Input
                  id="contact_role"
                  value={jobForm.event_briefing.contact_role}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, contact_role: e.target.value } })}
                  placeholder="Site Captain"
                  data-testid="briefing-contact-role"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={jobForm.event_briefing.contact_phone}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, contact_phone: e.target.value } })}
                  placeholder="+1 (404) 555-0142"
                  data-testid="briefing-contact-phone"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={jobForm.event_briefing.contact_email}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, contact_email: e.target.value } })}
                  placeholder="maria@ivystone.com"
                  data-testid="briefing-contact-email"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="sent_home_policy">Sent-Home Policy / Warnings</Label>
                <Textarea
                  id="sent_home_policy"
                  value={jobForm.event_briefing.sent_home_policy}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, sent_home_policy: e.target.value } })}
                  placeholder="Late arrival, missing all-black uniform, or inappropriate grooming → sent home without pay."
                  rows={2}
                  data-testid="briefing-sent-home"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="reporting_instructions">Reporting Instructions</Label>
                <Textarea
                  id="reporting_instructions"
                  value={jobForm.event_briefing.reporting_instructions}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, reporting_instructions: e.target.value } })}
                  placeholder="3 photos per shift, in-app recap form, reimbursements submitted by 8pm same day…"
                  rows={2}
                  data-testid="briefing-reporting"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="required_documents">Required Documents</Label>
                <Input
                  id="required_documents"
                  value={jobForm.event_briefing.required_documents}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, required_documents: e.target.value } })}
                  placeholder="W-9, Driver's License (comma-separated)"
                  data-testid="briefing-documents"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="certifications">Required Certifications</Label>
                <Input
                  id="certifications"
                  value={jobForm.event_briefing.certifications}
                  onChange={(e) => setJobForm({ ...jobForm, event_briefing: { ...jobForm.event_briefing, certifications: e.target.value } })}
                  placeholder="TIPS, ServSafe (comma-separated)"
                  data-testid="briefing-certifications"
                />
              </div>
              {/* ───────────────────────────────────────────────────────────── */}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowJobForm(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={posting}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="submit-job-btn"
              >
                {posting ? 'Posting...' : 'Post Job'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Job Details Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedJob.title}</DialogTitle>
                <DialogDescription>
                  {selectedJob.role_name} • {selectedJob.city}, {selectedJob.state}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Job Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium">{formatDateMonthDay(selectedJob.job_date)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-medium">{formatTimeRange(selectedJob.start_time, selectedJob.end_time)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Pay Rate</p>
                    <p className="font-medium text-cyan-600">${selectedJob.hourly_rate}/hr</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Talent</p>
                    <p className="font-medium">{selectedJob.applications?.filter(a => a.status === 'accepted').length || 0}/{selectedJob.workers_needed} hired</p>
                  </div>
                </div>

                {/* AI Requirement Organizer */}
                <RequirementOrganizer basePath={`/agency/jobs/${selectedJob.id}`} entityLabel="job" />


                {/* Applicants — withdrawn are separated from the active list */}
                <div>
                  {(() => {
                    const activeApps = (selectedJob.applications || []).filter((a) => a.status !== 'withdrawn');
                    const withdrawnApps = (selectedJob.applications || []).filter((a) => a.status === 'withdrawn');
                    return (
                      <>
                        <h3 className="font-medium mb-3">Applicants ({activeApps.length})</h3>
                        {activeApps.length === 0 ? (
                          <p className="text-sm text-gray-500">No active applicants</p>
                        ) : (
                          <div className="space-y-3">
                            {activeApps.map((app) => (
                        <Card key={app.id}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-3">
                                <Avatar>
                                  {app.talent_photo ? (
                                    <AvatarImage src={app.talent_photo} />
                                  ) : null}
                                  <AvatarFallback>
                                    {app.talent_name?.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{app.talent_name}</p>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <MapPin className="w-3 h-3" />
                                    <span>{app.talent_location}</span>
                                    <Star className="w-3 h-3 ml-2 text-yellow-500" />
                                    <span>{app.reliability_score}% reliability</span>
                                  </div>
                                  {app.cover_note && (
                                    <p className="text-sm text-gray-600 mt-1">&ldquo;{app.cover_note}&rdquo;</p>
                                  )}
                                </div>
                              </div>
                              <div>
                                {app.status === 'accepted' ? (
                                  <Badge className="bg-green-100 text-green-800">Accepted</Badge>
                                ) : app.status === 'pending' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handleAcceptApplicant(selectedJob.id, app.id)}
                                    className="bg-cyan-500 hover:bg-cyan-600"
                                    data-testid="accept-applicant-btn"
                                  >
                                    Accept
                                  </Button>
                                ) : (
                                  <Badge variant="outline">{app.status}</Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                            ))}
                          </div>
                        )}
                        {withdrawnApps.length > 0 && (
                          <div className="mt-4" data-testid="withdrawn-applicants-section">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">
                              Withdrawn ({withdrawnApps.length})
                            </h4>
                            <div className="space-y-2">
                              {withdrawnApps.map((app) => (
                                <div
                                  key={app.id}
                                  className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-gray-50 opacity-75"
                                  data-testid={`withdrawn-applicant-${app.id}`}
                                >
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">{app.talent_name}</p>
                                    <p className="text-xs text-gray-500">{app.talent_location}</p>
                                  </div>
                                  <Badge className="bg-gray-200 text-gray-700">Withdrawn</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription modal REMOVED per user request — Subscription management
          remains between iEvntür and the agency, not exposed in the workspace UI. */}
    </div>
  );
};

export default AgencyDashboard;
